import { useEffect } from 'react';

import { markWidgetViewed } from '../lib/widgetViewedStore';

// A widget counts as "viewed" once most of it is on screen inside the pane.
// Widgets taller than the pane can never hit a high intersectionRatio, so also
// accept covering most of the pane's own height.
const VIEWED_RATIO = 0.6;
const OBSERVER_THRESHOLDS = [0, 0.25, 0.5, VIEWED_RATIO, 0.75, 1];

// Watches the widgets pane and records when widget cards are actually seen,
// which is what decays the feed's announcement chips from tinted to quiet.
// Cards are matched by their [data-widget-id] attribute; a MutationObserver
// keeps the watch list current as widgets mount, reorder, or unmount.
export function useWidgetViewedObserver(paneRef, enabled = true) {
  useEffect(() => {
    if (!enabled) return undefined;
    const pane = paneRef.current;
    if (!pane || typeof IntersectionObserver === 'undefined') return undefined;

    // The closed pane slides off-screen via margin/transform but keeps layout,
    // so root-relative intersections still fire. Only count views while the
    // pane itself is actually within the viewport.
    const paneIsOnScreen = () => {
      const rect = pane.getBoundingClientRect();
      return rect.width > 0 && rect.right > 0 && rect.left < window.innerWidth;
    };

    // Each element's first callback reports its resting state at observe time,
    // not a user action — a widget already sitting on screen at load shouldn't
    // count as viewed (it would decay chips before the user ever looks).
    // Only transitions after that (scrolling a widget into view) mark it.
    const reportedInitialState = new WeakSet();

    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        const actionable = entries.filter((entry) => {
          if (reportedInitialState.has(entry.target)) return true;
          reportedInitialState.add(entry.target);
          return false;
        });

        if (!actionable.some((entry) => entry.isIntersecting)) return;
        if (!paneIsOnScreen()) return;

        for (const entry of actionable) {
          if (!entry.isIntersecting) continue;

          const cardHeight = entry.boundingClientRect.height || 0;
          const paneHeight = entry.rootBounds?.height || 0;
          const visibleHeight = entry.intersectionRect.height || 0;
          const paneCoverage = paneHeight > 0 ? visibleHeight / paneHeight : 0;
          if (entry.intersectionRatio < VIEWED_RATIO && !(cardHeight > paneHeight && paneCoverage >= VIEWED_RATIO)) continue;

          const widgetId = entry.target.getAttribute('data-widget-id');
          if (widgetId) markWidgetViewed(widgetId);
        }
      },
      { root: pane, threshold: OBSERVER_THRESHOLDS },
    );

    const observed = new Set();
    const observeCards = () => {
      pane.querySelectorAll('[data-widget-id]').forEach((element) => {
        if (observed.has(element)) return;
        observed.add(element);
        intersectionObserver.observe(element);
      });
    };

    observeCards();
    const mutationObserver = new MutationObserver(observeCards);
    mutationObserver.observe(pane, { childList: true, subtree: true });

    return () => {
      mutationObserver.disconnect();
      intersectionObserver.disconnect();
    };
  }, [paneRef, enabled]);
}
